import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Data, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { CommonRoutingSegments, NotificationService, RoleAccessor, UserService } from '../services';
import { RoleName } from '../user';

@Injectable({ providedIn: 'root' })
export class RoleGuard {
    public static readonly AcceptedRolesParamName = 'accptedRoles';

    public static addRolesToRouteData(roles: RoleName[], currentData: object = {}): RoleGuardData {
        return {
            ...currentData,
            accptedRoles: roles,
        };
    }

    constructor(
        private notification: NotificationService,
        private roleAccessor: RoleAccessor,
        private router: Router,
        private userService: UserService
    ) {}

    public async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
        if (!(await this.userService.isLoggedIn())) {
            return this.router.createUrlTree([CommonRoutingSegments.Login]);
        }
        if (this.isValid(route.data)) {
            return true;
        }
        this.notification.errorTranslate('COMMON.NOTALLOWED');
        return this.router.createUrlTree([CommonRoutingSegments.Dashboard]);
    }

    public async canLoad(route: Route, segments: UrlSegment[]): Promise<boolean> {
        return (await this.userService.isLoggedIn()) && this.isValid(route.data);
    }

    private isValid(routeData: Data): boolean {
        const allowedRoles = this.getAcceptedRolesFromRouteData(routeData);
        const currentRole = this.roleAccessor.getRoleData().roleId;
        return allowedRoles.includes(currentRole);
    }

    private getAcceptedRolesFromRouteData(routeData: Data): RoleName[] {
        const roles = routeData ? routeData[RoleGuard.AcceptedRolesParamName] : undefined;
        if (typeof roles === 'string') {
            return [roles as unknown as RoleName];
        } else if (Array.isArray(roles)) {
            return roles;
        } else {
            return [];
        }
    }
}

export interface RoleGuardData {
    [RoleGuard.AcceptedRolesParamName]?: RoleName[];
}
